import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProductBySlug, getProducts } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';

const ComponentDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    setLoading(true);
    setActiveSlide(0);
    getProductBySlug(slug).then(data => {
      if (data && data.data) {
        setProduct(data.data);
      } else if (data && data.id) {
        setProduct(data);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (product) {
      getProducts({ category: 'components', per_page: 8 }).then(data => {
        if (data && data.length > 0) {
          const filtered = data.filter(p => p.id !== product.id).slice(0, 4);
          setRelatedProducts(filtered);
        }
      });
    }
  }, [product]);

  const handleInquire = () => {
    const message = `Hello AFS, I am interested in the following product:\nName Component: ${product?.name || ''}\nPlease send me more information. Thank you.`;
    sessionStorage.setItem('contact_prefill', JSON.stringify({
      subject: 'Product Inquiry',
      message,
    }));
    navigate('/contact');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={{paddingTop: '64px'}}>
          <section className="cd-section">
            <div className="wrap">
              <div className="comp-loading">Loading...</div>
            </div>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header />
        <div style={{paddingTop: '64px'}}>
          <section className="cd-section">
            <div className="wrap">
              <div className="comp-empty">
                <p>Product not found.</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div style={{paddingTop: '64px'}}>
        <section className="cd-section">
          <div className="wrap">
            <div className="cd-grid">
              {/* Image Slider */}
              <div className="cd-image-wrap">
                {product.images && product.images.length > 0 ? (
                  <>
                    <div className="cd-slider">
                      <img src={product.images[activeSlide]?.url || product.primary_image_url} alt={product.name} className="cd-image" />
                      {product.images.length > 1 && (
                        <>
                          <button className="cd-slider-btn cd-slider-prev" onClick={() => setActiveSlide(prev => prev <= 0 ? product.images.length - 1 : prev - 1)} aria-label="Previous">&#8249;</button>
                          <button className="cd-slider-btn cd-slider-next" onClick={() => setActiveSlide(prev => prev >= product.images.length - 1 ? 0 : prev + 1)} aria-label="Next">&#8250;</button>
                        </>
                      )}
                    </div>
                    {product.images.length > 1 && (
                      <div className="cd-thumbs">
                        {product.images.map((img, i) => (
                          <button key={img.id || i} className={`cd-thumb ${i === activeSlide ? 'active' : ''}`} onClick={() => setActiveSlide(i)}>
                            <img src={img.url} alt={`${product.name} ${i + 1}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : product.primary_image_url ? (
                  <div className="cd-slider">
                    <img src={product.primary_image_url} alt={product.name} className="cd-image" />
                  </div>
                ) : (
                  <div className="cd-image-placeholder">
                    <span>&#9881;&#65039;</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="cd-info">
                <div className="cd-breadcrumb">
                  <a href="/components">Components</a> <span>/</span> <span>{product.name}</span>
                </div>
                <h1 className="cd-title">{product.name}</h1>

                <div className="cd-meta">
                  {product.brand?.name && (
                    <span className="cd-meta-item">
                      <strong>Brand:</strong> {product.brand.name}
                    </span>
                  )}
                  {product.category?.name && (
                    <span className="cd-meta-item">
                      <strong>Category:</strong> {product.category.name}
                    </span>
                  )}
                  {product.type_unit?.name && (
                    <span className="cd-meta-item">
                      <strong>Type:</strong> {product.type_unit.name}
                    </span>
                  )}
                  {product.sku && (
                    <span className="cd-meta-item">
                      <strong>SKU:</strong> {product.sku}
                    </span>
                  )}
                  <span className="cd-meta-item">
                    <strong>Stock:</strong> {product.quantity || 0} unit
                  </span>
                </div>

                {product.short_description && (
                  <p className="cd-short-desc">{product.short_description}</p>
                )}

                {product.description && (
                  <div className="cd-description" dangerouslySetInnerHTML={{ __html: product.description }} />
                )}

                <div className="cd-actions">
                  <button className="btn btn-y" onClick={handleInquire}>
                    Inquire Now &#8599;
                  </button>
                  <a href="/components" className="btn btn-dark">
                    &#8592; Back to Components
                  </a>
                </div>
              </div>
            </div>

            {/* Related Products */}
            {relatedProducts.length > 0 && (
              <div className="cd-related">
                <h2 className="cd-related-title">Other Products</h2>
                <div className="cd-related-grid">
                  {relatedProducts.map((p) => (
                    <Link to={`/components/${p.slug}`} className="cd-related-card" key={p.id}>
                      <div className="cd-related-img">
                        {p.primary_image_url ? (
                          <img src={p.primary_image_url} alt={p.name} />
                        ) : (
                          <div className="cd-related-placeholder">⚙️</div>
                        )}
                      </div>
                      <div className="cd-related-body">
                        <span className="cd-related-cat">{[p.brand?.name, p.type_unit?.name].filter(Boolean).join(' · ') || 'Component'}</span>
                        <h3 className="cd-related-name">{p.name}</h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
      <WhatsAppFloat />
    </>
  );
};

export default ComponentDetailPage;
